import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArchive, faCopy, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import { Button, Tools } from '@domain-group/suit-theme'

import useAgencyOrderBundle from '@hooks/useAgencyOrderBundle'
import type { UseOrderBundlePayload } from '@hooks/useOrderBundle'

import { confirmDelete } from '@functions/entity'

import type { OrderBundleModel } from '@models/types'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

interface ConfirmArchivePayload {
  orderBundle: OrderBundleModel,
  updateFn: Function,
  updateOrderBundle: (payload: object) => Promise<object>,
}

const confirmArchive = (params: ConfirmArchivePayload) => {
  const {
    orderBundle: {
      hidden,
      id,
    },
    updateFn,
    updateOrderBundle,
  } = params

  const archivedState = hidden ? 'unarchive' : 'archive'
  const archivedStateUpcase = archivedState[0].toUpperCase() + archivedState.slice(1)

  const updateOrderBundlePayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: { id, hidden: !hidden },
    toastText: `Order Bundle ${archivedStateUpcase}d`,
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: `Yes, ${archivedState} it.`,
      icon: 'warning',
      showCancelButton: true,
      text: `This will ${archivedState} the order bundle`,
      title: `${archivedStateUpcase} Order Bundle?`,
    })
    .then(({ value }) => {
      if (value){
        updateOrderBundle(updateOrderBundlePayload)
      }
    })
}

type OrderBundleToolsProps = {
  orderBundlePayload: UseOrderBundlePayload,
  updateOrderBundle: (payload: object) => Promise<object>,
}

const OrderBundleTools = (props: OrderBundleToolsProps) => {
  const { orderBundlePayload, updateOrderBundle } = props

  const {
    callbacks: {
      launchDuplicateOrderBundleModal,
      updateOrderBundle: updateFn,
    },
    orderBundle,
    orderBundle: {
      hidden,
    },
    loading,
    updating,
  } = orderBundlePayload

  const {
    callbacks: { bulkDeleteAgencyOrderBundles },
  } = useAgencyOrderBundle({ order_bundle_id: orderBundle.id })

  const confirmDeleteAllParams = {
    callbacks: {
      deleteFn: bulkDeleteAgencyOrderBundles,
    },
    options: {
      entityParams: { order_bundle_id: orderBundle.id },
      isBulkDelete: true,
    },
    strings: {
      confirmString: 'Are you sure? This will remove all assigned agencies, including any not currently shown.',
      deleteString: 'remove',
      entityName: 'Agencies',
    },
  }

  return (
    <Tools>
      <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faArchive} />}
          loading={updating}
          // @ts-expect-error TS2322
          marginBottom="medium"
          onClick={() => confirmArchive({ orderBundle, updateOrderBundle, updateFn })}
          size="medium"
        >
          {hidden ? 'Unarchive Bundle' : 'Archive Bundle'}
        </Button>
      </ScopedConditionalContent>

      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faCopy} />}
        loading={loading}
        // @ts-expect-error TS2322
        marginBottom="medium"
        onClick={() => launchDuplicateOrderBundleModal()}
        size="medium"
      >
        Duplicate Bundle
      </Button>

      <Button
        buttonStyle="secondaryUtility"
        icon={<FontAwesomeIcon icon={faTrashAlt} />}
        loading={loading}
        // @ts-expect-error TS2345
        onClick={() => confirmDelete(confirmDeleteAllParams)}
        size="medium"
      >
        Remove All Agencies
      </Button>
    </Tools>
  )
}

export default OrderBundleTools
